import { useEffect, useRef, useState } from "react";
import { Volume2, VolumeX, Play, X } from "lucide-react";
import { IMAGES } from "@/lib/images";
import { api } from "@/lib/api";

export const HeroPrayerVideo = () => {
  const loopRef = useRef(null);
  const fullRef = useRef(null);
  const [cover, setCover] = useState(null);
  const [praying, setPraying] = useState(false);
  const [muted, setMuted] = useState(true);

  useEffect(() => {
    api
      .get("/spoken-prayers/cover")
      .then((r) => {
        if (r.data && r.data.video_url) setCover(r.data);
      })
      .catch(() => {});
  }, []);

  const startPray = () => {
    setPraying(true);
    setTimeout(() => {
      const v = fullRef.current;
      if (v) {
        v.currentTime = 0;
        v.muted = false;
        const p = v.play();
        if (p && p.catch) p.catch(() => {});
      }
    }, 60);
  };

  const stopPray = () => {
    const v = fullRef.current;
    if (v) v.pause();
    setPraying(false);
  };

  const toggleLoopSound = () => {
    const v = loopRef.current;
    if (!v) return;
    const next = !muted;
    v.muted = next;
    setMuted(next);
    const p = v.play();
    if (p && p.catch) p.catch(() => {});
  };

  return (
    <div className="relative h-[460px] sm:h-[560px] w-full overflow-hidden">
      {/* Ambient short loop (muted) */}
      <video
        ref={loopRef}
        data-testid="hero-prayer-video"
        poster={IMAGES.portrait}
        autoPlay
        muted
        loop
        playsInline
        preload="auto"
        className={`h-full w-full object-cover object-center bg-ink transition-opacity duration-500 ${praying ? "opacity-0 pointer-events-none" : "opacity-100"}`}
      >
        <source src={IMAGES.heroPrayerVideoWebm} type="video/webm" />
        <source src={IMAGES.heroPrayerVideo} type="video/mp4" />
      </video>

      {/* Full live prayer — click to play */}
      {cover?.video_url && (
        <video
          ref={fullRef}
          data-testid="hero-full-prayer-video"
          src={cover.video_url}
          playsInline
          controls={praying}
          onEnded={stopPray}
          className={`absolute inset-0 h-full w-full object-cover object-center bg-ink transition-opacity duration-500 ${praying ? "opacity-100" : "opacity-0 pointer-events-none"}`}
        />
      )}

      <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-ink/70 via-transparent to-transparent" />

      {/* Primary control */}
      {!praying && cover?.video_url && (
        <button
          type="button"
          onClick={startPray}
          data-testid="hero-pray-with-me"
          className="group absolute bottom-4 left-4 inline-flex items-center gap-2 rounded-full bg-gold px-5 py-2.5 text-sm font-semibold text-ink transition-colors hover:bg-gold-light"
        >
          <Play size={16} className="fill-current" /> Pray with me
        </button>
      )}

      {/* Fallback: hear the short loop when no full prayer is set */}
      {!praying && !cover?.video_url && (
        <button
          type="button"
          onClick={toggleLoopSound}
          data-testid="hero-prayer-sound-toggle"
          aria-label={muted ? "Play the prayer with sound" : "Mute the prayer"}
          className="group absolute bottom-4 left-4 inline-flex items-center gap-2 rounded-full border border-gold/50 bg-ink/70 px-4 py-2 text-sm font-semibold text-parchment backdrop-blur-sm transition-colors hover:border-gold hover:text-gold-light"
        >
          {muted ? <Volume2 size={16} className="text-gold-light" /> : <VolumeX size={16} className="text-gold-light" />}
          {muted ? "Tap to hear the prayer" : "Praying · tap to mute"}
        </button>
      )}

      {praying && (
        <button
          type="button"
          onClick={stopPray}
          data-testid="hero-pray-close"
          className="absolute top-4 right-4 inline-flex items-center gap-1 rounded-full border border-parchment/40 bg-ink/70 px-3 py-1.5 text-xs font-semibold text-parchment backdrop-blur-sm transition-colors hover:border-gold"
        >
          <X size={14} /> Close
        </button>
      )}

      {!praying && (
        <span className="absolute right-4 top-4 flex h-2.5 w-2.5">
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-gold/70" />
          <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-gold" />
        </span>
      )}
    </div>
  );
};
